"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FavoritesProvider = void 0;
var _auth = require("@ori/auth");
var _configurationManager = require("@ori/configuration-manager");
var _environment = require("@ori/environment");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _presentationHooks = require("@ori/presentation-hooks");
var _testidGenerator = require("@ori/testid-generator");
var _react = require("react");
var _FavoritesDialog = require("../components/FavoritesDialog");
var _constantsGenerated = require("../constants-generated");
var _hooks = require("../hooks");
var _favoritesContext = require("./favoritesContext");
var _types = require("./types");
var _jsxRuntime = require("react/jsx-runtime");
/** Provides the favorites context for logged-in users. Requires wrapping with EnvironmentProvider, TranslationsProvider, UniversalTokenProvider, and ServiceUrlsProvider with globalApiUrl and staticApiUrl specified. */
const FavoritesProvider = ({
  children
}) => {
  const {
    token
  } = (0, _auth.useToken)();
  const {
    globalApiUrl
  } = (0, _configurationManager.useServiceUrls)(['globalApi']);
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    open,
    onOpen: openDialog,
    onClose
  } = (0, _presentationHooks.useDisclose)();
  const [lastUsedProductCode, setLastUsedProductCode] = (0, _react.useState)('');
  const [lastUsedProductCodeInLists, setLastUsedProductCodeInLists] = (0, _react.useState)([]);
  const [favoritesDialogMode, setFavoritesDialogMode] = (0, _react.useState)(_types.FavoritesDialogMode.Default);
  const [lastUsedListId, setLastUsedListId] = (0, _react.useState)('');
  const [prefilledProductCodes, setPrefilledProductCodes] = (0, _react.useState)([]);
  const [shouldRedirectOnSuccess, setShouldRedirectOnSuccess] = (0, _react.useState)(false);
  const {
    initialized,
    lists,
    maximumAllowedPersonalLists,
    requestLists,
    toggleProductInLists,
    toggleProductsInLists,
    updateLists
  } = (0, _hooks.useGetFavorites)();
  const {
    openSnackbar,
    snackbar
  } = (0, _hooks.useSnackbar)();
  const isProductInList = (0, _react.useCallback)(({
    productCode,
    productListId
  }) => lists.some(({
    id,
    productCodes
  }) => id === productListId && productCodes.includes(productCode)), [lists]);
  const isProductInLists = (0, _react.useCallback)(productCode => lists.some(({
    productCodes
  }) => productCodes.includes(productCode)), [lists]);
  const getList = (0, _react.useCallback)(id => lists.find(list => list.id === id), [lists]);
  const getProductListIds = (0, _react.useCallback)((productCode = lastUsedProductCode) => lists.filter(({
    productCodes
  }) => productCodes.includes(productCode)).map(({
    id
  }) => id), [lastUsedProductCode, lists]);
  const onFavoritesClick = (0, _react.useCallback)(({
    productCode
  }) => {
    setFavoritesDialogMode(_types.FavoritesDialogMode.Default);
    setLastUsedProductCode(productCode);
    setLastUsedProductCodeInLists(getProductListIds(productCode));
    openDialog();
  }, [getProductListIds, openDialog]);
  const createList = (0, _react.useCallback)(({
    productCodes,
    shouldRedirectOnSuccess: shouldRedirectOnSuccessParam
  }) => {
    setFavoritesDialogMode(_types.FavoritesDialogMode.Create);
    setLastUsedListId('');
    setPrefilledProductCodes(productCodes);
    setShouldRedirectOnSuccess(shouldRedirectOnSuccessParam);
    openDialog();
  }, [openDialog]);
  const deleteList = (0, _react.useCallback)(({
    id,
    shouldRedirectOnSuccess: shouldRedirectOnSuccessParam
  }) => {
    setFavoritesDialogMode(_types.FavoritesDialogMode.Delete);
    setLastUsedListId(id);
    setShouldRedirectOnSuccess(shouldRedirectOnSuccessParam);
    openDialog();
  }, [openDialog]);
  const editList = (0, _react.useCallback)(({
    id,
    shouldRedirectOnSuccess: shouldRedirectOnSuccessParam
  }) => {
    setFavoritesDialogMode(_types.FavoritesDialogMode.Edit);
    setLastUsedListId(id);
    setShouldRedirectOnSuccess(shouldRedirectOnSuccessParam);
    openDialog();
  }, [openDialog]);
  const value = (0, _react.useMemo)(() => ({
    initialized,
    maximumAllowedPersonalLists,
    lastUsedProductCode,
    lastUsedProductCodeInLists,
    lists,
    createList,
    deleteList,
    editList,
    getList,
    getProductListIds,
    isProductInList,
    isProductInLists,
    onFavoritesClick,
    openSnackbar,
    requestLists,
    toggleProductInLists,
    toggleProductsInLists,
    updateLists
  }), [initialized, maximumAllowedPersonalLists, lastUsedProductCode, lastUsedProductCodeInLists, lists, createList, deleteList, editList, getList, getProductListIds, isProductInList, isProductInLists, onFavoritesClick, openSnackbar, requestLists, toggleProductInLists, toggleProductsInLists, updateLists]);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_favoritesContext.Provider, {
    value: value,
    children: [children, /*#__PURE__*/(0, _jsxRuntime.jsxs)(_testidGenerator.TestIdProvider, {
      team: _testidGenerator.Team.Presentation,
      project: _constantsGenerated.APP_NAME,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_globalApiClientReact.ApiClientProvider, {
        baseUrl: globalApiUrl,
        token: token,
        tenant: tenant,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_FavoritesDialog.FavoritesDialog, {
          lastUsedListId: lastUsedListId,
          mode: favoritesDialogMode,
          open: open,
          prefilledProductCodes: prefilledProductCodes,
          shouldRedirectOnSuccess: shouldRedirectOnSuccess,
          onClose: onClose
        })
      }), snackbar]
    })]
  });
};
exports.FavoritesProvider = FavoritesProvider;
